import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Brain,
  Eye,
  Pencil,
  Save,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { z } from 'zod';
import { ApiError } from '@/lib/api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { CostLimitBanner } from '@/components/ui/CostLimitBanner';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TokenStatsBadge } from '@/components/ui/TokenStatsBadge';
import { ValidationPanel } from '@/components/ui/ValidationPanel';
import { YamlEditor } from '@/components/ui/YamlEditor';
import { YamlViewer } from '@/components/ui/YamlViewer';
import {
  useAiAccept,
  useAiSuggest,
  useAiValidate,
  type AiSuggestResponse,
  type ValidationResult,
} from '@/hooks/useAi';
import { useSystems } from '@/hooks/useSystems';

/**
 * /ai/suggest - KI-Routinen-Generator (Sprint 6, WT-602/WT-603/WT-604).
 *
 * Workflow:
 *   1. Eingabe-Card: System + Intent -> POST /ai/suggest-routine
 *   2. Vorschlag-Card: Token-Stats + Playbook (Viewer/Editor) + ValidationPanel
 *   3. Erneut validieren (POST /ai/validate-playbook) bei Bearbeitung
 *   4. Speichern (POST /ai/suggest-routine/{id}/accept) - nur wenn alles grün
 *
 * Re-Confirm-Dialog bei is_destructive=true.
 */

const PLACEHOLDER_INTENTS = [
  'Update Nextcloud auf neueste Stable',
  'Backup-Job für /var/lib/postgresql prüfen',
  'OPNsense-Firewall auf 24.7 patchen',
  'Asterisk-Logs rotieren und alte VoIP-Aufnahmen archivieren',
];

const searchSchema = z.object({
  system: z.string().uuid().optional(),
  customer: z.string().uuid().optional(),
});

export const Route = createFileRoute('/ai/suggest')({
  component: AiSuggestPage,
  validateSearch: (s) => searchSchema.parse(s),
});

function AiSuggestPage(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const systemsQuery = useSystems({ limit: 200 });
  const suggest = useAiSuggest();
  const validate = useAiValidate();

  const [systemId, setSystemId] = useState<string | null>(search.system ?? null);
  const [intent, setIntent] = useState('');
  const [response, setResponse] = useState<AiSuggestResponse | null>(null);
  const [editing, setEditing] = useState(false);
  // Editiert wird die strukturierte JSON-Form (PlaybookProposal), weil das
  // Backend validate/accept genau diese erwartet und JSON verlustfrei geparst
  // werden kann (kein YAML-Parser im Bundle).
  const [draftJson, setDraftJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [liveValidation, setLiveValidation] = useState<ValidationResult | null>(null);
  const [showDestructiveDialog, setShowDestructiveDialog] = useState(false);
  const [acceptError, setAcceptError] = useState<Error | null>(null);

  const accept = useAiAccept(response?.session_id ?? null);

  const placeholder = useMemo(
    () => PLACEHOLDER_INTENTS[Math.floor(Math.random() * PLACEHOLDER_INTENTS.length)] ?? '',
    []
  );

  const systemOptions = useMemo(() => {
    const systems = systemsQuery.data?.data ?? [];
    return systems.map((s) => ({
      value: s.id,
      label: s.display_name ?? s.hostname,
      hint: s.hostname,
    }));
  }, [systemsQuery.data]);

  // Ziel-System ist Backend-Pflicht (system_id: UUID) und der Intent braucht
  // >= 10 Zeichen (Backend: Field(min_length=10)).
  const trimmedIntent = intent.trim();
  const intentTooShort = trimmedIntent.length > 0 && trimmedIntent.length < 10;
  const canSubmit =
    Boolean(systemId) && trimmedIntent.length >= 10 && !suggest.isPending;

  // Beim Editieren muss der User erneut validieren, bevor gespeichert werden
  // kann: liveValidation wird bei jeder Änderung zurückgesetzt, sodass die
  // Validierung nicht stale grün bleibt.
  const currentValidation =
    liveValidation ?? (editing ? null : (response?.validation ?? null));

  /** Parst den JSON-Editor-Inhalt; setzt jsonError bei Fehlern. */
  const parseDraftJson = (): Record<string, unknown> | null => {
    try {
      const parsed: unknown = JSON.parse(draftJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setJsonError('Das Playbook-JSON muss ein Objekt sein.');
        return null;
      }
      setJsonError(null);
      return parsed as Record<string, unknown>;
    } catch (err) {
      setJsonError(
        'Playbook-JSON ist nicht parsebar: ' +
          (err instanceof Error ? err.message : String(err))
      );
      return null;
    }
  };

  /** Liefert das strukturierte playbook_json für validate/accept. */
  const currentPlaybookJson = (): Record<string, unknown> | null => {
    if (!response) return null;
    return editing ? parseDraftJson() : response.playbook_json;
  };

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!systemId || trimmedIntent.length < 10) return;
    setAcceptError(null);
    setLiveValidation(null);
    setJsonError(null);
    suggest.mutate(
      { system_id: systemId, customer_id: search.customer ?? null, intent: trimmedIntent },
      {
        onSuccess: (res) => {
          setResponse(res);
          setDraftJson(JSON.stringify(res.playbook_json, null, 2));
          setEditing(false);
        },
      }
    );
  };

  const onRevalidate = (): void => {
    if (!response) return;
    const pj = currentPlaybookJson();
    if (!pj) return;
    validate.mutate(
      { playbook_json: pj },
      { onSuccess: (res) => setLiveValidation(res.validation) }
    );
  };

  const onAcceptClick = (): void => {
    if (!response) return;
    if (response.is_destructive) {
      setShowDestructiveDialog(true);
      return;
    }
    void doAccept();
  };

  const doAccept = async (): Promise<void> => {
    if (!response) return;
    setShowDestructiveDialog(false);
    setAcceptError(null);
    const pj = currentPlaybookJson();
    if (!pj) {
      setAcceptError(new Error(jsonError ?? 'Playbook-JSON ist ungültig.'));
      return;
    }
    try {
      // Backend leitet display_name/description/is_destructive aus dem
      // Proposal ab - wir senden nur das strukturierte playbook_json.
      const res = await accept.mutateAsync({ playbook_json: pj });
      void navigate({ to: '/routines/$slug', params: { slug: res.routine_slug } });
    } catch (err) {
      setAcceptError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const onDiscard = (): void => {
    setResponse(null);
    setIntent('');
    setEditing(false);
    setDraftJson('');
    setJsonError(null);
    setLiveValidation(null);
    setAcceptError(null);
  };

  const allValid = currentValidation
    ? currentValidation.schema_valid &&
      currentValidation.allowlist_valid &&
      currentValidation.syntax_valid
    : false;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="size-5 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-semibold">KI-Routinen-Generator</h1>
          </div>
          <p className="text-muted">
            Claude Opus 4.7 liest Service-Profil und Wiki-Doku und schlägt ein maßgeschneidertes
            Wartungs-Playbook vor &mdash; nichts läuft ohne Confirm + Snapshot.
          </p>
        </div>
      </header>

      <CostLimitBanner />

      {/* ---------- Eingabe-Card ---------- */}
      <Card title="Was soll die KI bauen?">
        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Combobox
              label="Ziel-System *"
              placeholder="System wählen ..."
              value={systemId}
              onChange={setSystemId}
              options={systemOptions}
              required
              hint="Pflichtfeld. Die KI braucht Service-Profil und Versions-Drift des Systems."
            />
            <div className="flex items-end">
              <p className="text-xs text-muted">
                Antwortzeit: ~10s. Nutzt Prompt-Caching, Cost ~$0.002 pro Anfrage.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="ai-intent" className="text-sm font-medium">
              Was soll die Routine tun?
            </label>
            <textarea
              id="ai-intent"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              rows={3}
              placeholder={placeholder}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="ai-intent-input"
            />
            <p className="text-xs text-muted">
              Beispiel: &laquo;{placeholder}&raquo;
            </p>
            {intentTooShort && (
              <p className="text-xs text-danger" role="alert">
                Mindestens 10 Zeichen erforderlich ({trimmedIntent.length}/10).
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="submit"
              leftIcon={<Sparkles className="size-4" />}
              loading={suggest.isPending}
              disabled={!canSubmit}
              data-testid="ai-suggest-submit"
            >
              KI-Vorschlag generieren
            </Button>
            {suggest.isPending && (
              <span className="text-xs text-muted">~10s &mdash; Claude denkt nach...</span>
            )}
            {!suggest.isPending && !systemId && (
              <span className="text-xs text-warning">
                Bitte zuerst ein Ziel-System wählen.
              </span>
            )}
          </div>

          {suggest.isError &&
            (suggest.error instanceof ApiError && suggest.error.status === 422 ? (
              <div
                role="alert"
                className="rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
              >
                <p className="font-medium">Eingabe unvollständig</p>
                <p className="mt-1 text-danger/90">
                  Ziel-System ist Pflicht und der Intent muss mindestens 10 Zeichen
                  lang sein.
                </p>
              </div>
            ) : (
              <ErrorAlert error={suggest.error} />
            ))}
        </form>
      </Card>

      {/* ---------- Vorschlag-Card ---------- */}
      {response && (
        <Card
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span>Vorschlag</span>
              <Badge tone="primary">{response.proposed_slug}</Badge>
              {response.is_destructive && (
                <Badge tone="danger">
                  <span className="inline-flex items-center gap-1">
                    <AlertTriangle className="size-3" /> destruktiv
                  </span>
                </Badge>
              )}
              {response.requires_snapshot && (
                <Badge tone="primary">Snapshot Pflicht</Badge>
              )}
            </span>
          }
          actions={<TokenStatsBadge usage={response.usage} compact />}
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted">{response.description_md}</p>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px]">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Generated Playbook</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={editing ? <Eye className="size-3" /> : <Pencil className="size-3" />}
                    onClick={() => {
                      if (editing) {
                        // Beim Toggle "view" -> Änderungen verwerfen.
                        setDraftJson(JSON.stringify(response.playbook_json, null, 2));
                        setJsonError(null);
                        setLiveValidation(null);
                      }
                      setEditing((v) => !v);
                    }}
                    data-testid="ai-edit-toggle"
                  >
                    {editing ? 'Vorschau' : 'Bearbeiten'}
                  </Button>
                </div>

                {editing ? (
                  <div className="flex flex-col gap-1">
                    <YamlEditor
                      label="Playbook JSON"
                      hideLabel
                      ariaLabel="Playbook JSON (strukturiert, bearbeitbar)"
                      value={draftJson}
                      onChange={(v) => {
                        setDraftJson(v);
                        // Jede Änderung invalidiert das letzte Ergebnis ->
                        // Speichern erst nach erneuter Validierung möglich.
                        setLiveValidation(null);
                        setJsonError(null);
                      }}
                    />
                    <p className="text-xs text-muted">
                      Strukturierte PlaybookProposal-Form. Das Backend validiert
                      und kompiliert daraus die YAML. Danach &laquo;Erneut
                      validieren&raquo; klicken.
                    </p>
                    {jsonError && (
                      <p className="text-xs text-danger" role="alert">
                        {jsonError}
                      </p>
                    )}
                  </div>
                ) : (
                  <YamlViewer source={response.playbook_yaml} />
                )}
              </div>

              <div className="flex flex-col gap-3">
                {currentValidation && <ValidationPanel validation={currentValidation} />}
                <TokenStatsBadge usage={response.usage} />
              </div>
            </div>

            {/* ---------- Aktion-Toolbar ---------- */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <Button
                variant="secondary"
                leftIcon={<Sparkles className="size-3" />}
                onClick={onRevalidate}
                loading={validate.isPending}
                disabled={!editing && Boolean(currentValidation)}
                title="POST /ai/validate-playbook"
              >
                Erneut validieren
              </Button>
              <Button
                leftIcon={<Save className="size-4" />}
                onClick={onAcceptClick}
                disabled={!allValid || accept.isPending}
                loading={accept.isPending}
                data-testid="ai-accept"
              >
                Als Routine speichern
              </Button>
              <Button
                variant="ghost"
                leftIcon={<Trash2 className="size-3" />}
                onClick={onDiscard}
              >
                Verwerfen
              </Button>
              {!allValid && (
                <span className="text-xs text-warning">
                  Speichern nur möglich wenn alle Validation-Checks grün sind.
                </span>
              )}
            </div>

            {validate.isError && <ErrorAlert error={validate.error} />}
            {accept.isError && <ErrorAlert error={accept.error} />}
            {acceptError && <ErrorAlert error={acceptError} />}
          </div>
        </Card>
      )}

      {/* ---------- Re-Confirm-Dialog bei destructive ---------- */}
      <Dialog
        open={showDestructiveDialog}
        onClose={() => setShowDestructiveDialog(false)}
        title="Destruktive KI-Routine speichern?"
        description="Die KI hat diese Routine als destruktiv eingestuft."
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDestructiveDialog(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              leftIcon={<AlertTriangle className="size-4" />}
              onClick={() => void doAccept()}
              loading={accept.isPending}
              data-testid="ai-accept-destructive-confirm"
            >
              Ja, trotzdem speichern
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm">
          <p>
            Diese Routine wurde als <strong>destruktiv</strong> markiert (z.B. weil sie
            Daten löscht, Migrations triggert oder Reboots auslöst).
          </p>
          <ul className="list-disc pl-5 text-muted">
            <li>Beim Echtlauf wird <strong>zwingend ein Snapshot</strong> erzeugt.</li>
            <li>Confirmation-Dialog bei Run-Start (wie bei jedem Run).</li>
            <li>
              Nach Speichern erscheint die Routine in <code>/routines</code> und kann
              normal über den Run-Wizard gestartet werden.
            </li>
          </ul>
        </div>
      </Dialog>
    </div>
  );
}
